import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { endpoints } from '../services/api';
import ResultadosBancarios from '../components/ResultadosBancarios';

const FASE_LABELS = { 1: 'Fase 1', 2: 'Fase 2', 3: 'Fase 3', 4: 'Fase 4', 5: 'Fase 5', 6: 'Fase 6' };

function formatCurrency(value) {
    const amount = Number.parseFloat(value);
    if (!Number.isFinite(amount)) return '-';
    return amount.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' });
}

export default function ExpedienteDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [expediente, setExpediente] = useState(null);
    const [pays, setPays] = useState([]);
    const [bancos, setBancos] = useState([]);
    const [resultadoFase2, setResultadoFase2] = useState(null);
    const [editingName, setEditingName] = useState(false);
    const [editName, setEditName] = useState('');
    const [editNotas, setEditNotas] = useState('');
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState('info');
    const [fase1Form, setFase1Form] = useState({ pay_id: '', bancos: [], fecha_obtencion: new Date().toISOString().split('T')[0] });
    const [fase2ResetNrbe, setFase2ResetNrbe] = useState('');
    const [fase3Form, setFase3Form] = useState({ nrbe: '', fecha_obtencion: new Date().toISOString().split('T')[0] });
    const [fase3ResetNrbe, setFase3ResetNrbe] = useState('');
    const [fase3Pendientes, setFase3Pendientes] = useState([]);
    const [fase3Result, setFase3Result] = useState(null);
    const [fase3Resumen, setFase3Resumen] = useState(null);
    const [fase3ResumenLoading, setFase3ResumenLoading] = useState(false);

    const [fase1Result, setFase1Result] = useState(null);

    const load = async () => {
        const r = await endpoints.expedientes.get(id);
        setExpediente(r.data);
        setLoading(false);
    };

    useEffect(() => { load(); }, [id]);

    const cargarPays = async () => {
        try {
            const r = await endpoints.n63.paysDisponibles(expediente.cr_id);
            setPays(r.data || []);
        } catch (err) {
            console.error('Error cargando PAYs:', err);
            alert('Error al cargar PAYs. Verifica la conexión con Odoo.');
        }
    };

    const cargarBancos = async () => {
        try {
            const r = await endpoints.n63.bancosDisponibles(expediente.cr_id);
            setBancos(r.data || []);
        } catch (err) {
            console.error('Error cargando bancos:', err);
            alert('Error al cargar bancos. Verifica la conexión con Odoo.');
        }
    };

    const generarFase1 = async () => {
        if (!fase1Form.pay_id || fase1Form.bancos.length === 0) return;
        const result = await endpoints.n63.generarFase1(id, {
            pay_id_odoo: fase1Form.pay_id,
            bancos: fase1Form.bancos.map(nrbe => ({ nrbe })),
            config_emisor: {
                nif_emisor: 'G19005297',
                ine_emisor: '000000',
                nombre_emisor: 'COMUNIDAD DE REGANTES DEL CANAL DE HENAR',
                fecha_obtencion: fase1Form.fecha_obtencion || undefined,
            },
        });
        setFase1Result(result.data);
        load();
    };

    const importarFase2 = async (file) => {
        const r = await endpoints.n63.importarFase2(id, file);
        setResultadoFase2(r.data);
        load();
    };

    const generarFase3 = async () => {
        if (!fase3Form.nrbe) return;
        const r = await endpoints.n63.generarFase3(id, {
            nrbe: fase3Form.nrbe,
            config_emisor: {
                nif_emisor: 'G19005297',
                ine_emisor: '000000',
                nombre_emisor: 'COMUNIDAD DE REGANTES DEL CANAL DE HENAR',
                fecha_obtencion: fase3Form.fecha_obtencion || undefined,
            },
        });
        setFase3Result(r.data);
        await cargarPendientesF3(fase3Form.nrbe);
        load();
    };

    const cargarPendientesF3 = async (nrbe) => {
        if (!nrbe) {
            setFase3Pendientes([]);
            setFase3Resumen(null);
            return;
        }

        setFase3ResumenLoading(true);
        try {
            const [pendientesRes, resumenRes] = await Promise.all([
                endpoints.n63.pendientesFase3(id, nrbe),
                endpoints.n63.resumenFase3(id, nrbe),
            ]);
            setFase3Pendientes(pendientesRes.data || []);
            setFase3Resumen(resumenRes.data || null);
        } finally {
            setFase3ResumenLoading(false);
        }
    };

    const verificarPays = async () => {
        await endpoints.n63.verificarPays(id);
        load();
    };

    if (loading) return <div>Cargando...</div>;
    if (!expediente) return <div>Expediente no encontrado</div>;

    const fases = expediente.fases || [];
    const fases2Disponibles = fases.filter(f => f.fase === 2);
    const fases3Disponibles = fases.filter(f => f.fase === 3);

    // Agrupar fases por número
    const fasesPorFase = {};
    for (let i = 1; i <= 3; i++) {
        fasesPorFase[i] = fases.filter(f => f.fase === i);
    }

    return (
        <div>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '16px' }}>
                <div style={{ flex: 1 }}>
                    {editingName ? (
                        <div>
                            <input type="text" value={editName}
                                onChange={e => setEditName(e.target.value)}
                                style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: 8, width: '100%' }} />
                            <textarea value={editNotas}
                                onChange={e => setEditNotas(e.target.value)}
                                placeholder="Notas del expediente..."
                                rows={2}
                                style={{ width: '100%', marginBottom: 8, fontSize: '0.85rem' }} />
                            <div style={{ display: 'flex', gap: 8 }}>
                                <button className="btn btn-sm btn-primary"
                                    onClick={async () => {
                                        await endpoints.expedientes.update(id, { nombre: editName, notas: editNotas });
                                        setEditingName(false);
                                        load();
                                    }}>Guardar</button>
                                <button className="btn btn-sm"
                                    onClick={() => setEditingName(false)}>Cancelar</button>
                            </div>
                        </div>
                    ) : (
                        <div>
                            <h1 style={{ margin: 0, cursor: 'pointer' }}
                                onClick={() => { setEditName(expediente.nombre); setEditNotas(expediente.notas || ''); setEditingName(true); }}
                                title="Click para editar nombre y notas">
                                {expediente.nombre} ✏️
                            </h1>
                            <p style={{ color: 'var(--color-text-muted)', margin: '4px 0 0' }}>
                                #{expediente.id} · {expediente.cr_id} · Creado {new Date(expediente.created_at).toLocaleDateString()}
                            </p>
                            {expediente.notas && (
                                <p style={{ color: 'var(--color-text-secondary)', margin: '4px 0 0', fontSize: '0.85rem', fontStyle: 'italic' }}>
                                    📝 {expediente.notas}
                                </p>
                            )}
                        </div>
                    )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <button className="btn btn-sm btn-secondary" onClick={() => navigate('/ayuda')}
                        title="Abrir ayuda técnica de Cuaderno 63 para revisar campos y posiciones">
                        📘 Ayuda N63
                    </button>
                    {expediente.estado !== 'archivado' && (
                        <button className="btn btn-sm btn-secondary" onClick={async () => {
                            if (!confirm('¿Archivar este expediente? No se borrará nada, solo se ocultará de la lista principal.')) return;
                            await endpoints.expedientes.archivar(id, true);
                            load();
                        }}
                            title="Archivar expediente (no se borra, se oculta)">
                            📦 Archivar
                        </button>
                    )}
                    <button className="btn btn-sm btn-secondary" onClick={verificarPays}
                        title="Verifica el estado de las PAYs asociadas en Odoo">
                        🔄 Verificar PAYs
                    </button>
                    <span className={`badge badge-${expediente.estado === 'abierto' ? 'open' : expediente.estado === 'en_proceso' ? 'progress' : 'done'}`}
                        style={{ fontSize: '0.9rem', padding: '4px 12px' }}>
                        {expediente.estado}
                    </span>
                </div>
            </div>

            {/* Phase Timeline */}
            <div className="phase-timeline">
                {[1, 2, 3, 4, 5, 6].map(f => (
                    <div key={f} className={`phase-step ${fasesPorFase[f]?.length > 0 ? 'done' : ''} ${f === 1 ? 'active' : ''}`}>
                        {FASE_LABELS[f]}
                        {fasesPorFase[f]?.length > 0 && (
                            <div style={{ fontSize: '0.7rem', opacity: 0.8 }}>
                                {fasesPorFase[f].length} banco(s)
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {/* Tabs */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '16px', borderBottom: '1px solid var(--border-color)', paddingBottom: '8px' }}>
                {[{
                    key: 'info', label: 'Información',
                    help: 'Datos generales del expediente y resumen de fases'
                }, {
                    key: 'fase1', label: 'Fase 1',
                    help: 'Generar ficheros de embargo iniciales por banco'
                }, {
                    key: 'fase2', label: 'Fase 2',
                    help: 'Importar respuesta bancaria con IBANs de los deudores'
                }, {
                    key: 'fase3', label: 'Fase 3',
                    help: 'Generar órdenes de embargo con los IBANs confirmados'
                }, {
                    key: 'resultados', label: 'Resultados bancarios',
                    help: 'Recepciones F4/F6, resultados por banco e histórico inmutable'
                }, {
                    key: 'logs', label: 'Trazabilidad',
                    help: 'Registro de todas las acciones del expediente'
                }].map(t => (
                    <button key={t.key} onClick={() => setTab(t.key)}
                        className={`btn btn-sm ${tab === t.key ? 'btn-primary' : 'btn-secondary'}`}
                        title={t.help}>
                        {t.label}
                    </button>
                ))}
            </div>

            {/* Tab: Info */}
            {tab === 'info' && (
                <div>
                    {/* Agrupar fases por NRBE */}
                    {(() => {
                        const porNrbe = {};
                        for (const f of fases) {
                            if (!porNrbe[f.nrbe_entity_code]) porNrbe[f.nrbe_entity_code] = {};
                            porNrbe[f.nrbe_entity_code][f.fase] = f;
                        }
                        
                        if (Object.keys(porNrbe).length === 0) {
                            return (
                                <div className="card" style={{ textAlign: 'center', padding: '40px' }}>
                                    <p style={{ color: 'var(--color-text-muted)' }}>Aún no hay fases en este expediente.</p>
                                    <p>Ve a la pestaña <strong>Fase 1</strong> para empezar.</p>
                                </div>
                            );
                        }

                        return Object.entries(porNrbe).map(([nrbe, fasesPorBanco]) => (
                            <div key={nrbe} className="card" style={{ marginBottom: 12 }}>
                                <h3 style={{ margin: '0 0 12px', fontSize: '0.95rem' }}>
                                    Banco {nrbe}
                                    <span style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', marginLeft: 12 }}>
                                        {bancos.length > 0 && bancos.find(b => b.code === nrbe)?.journal_name || ''}
                                    </span>
                                </h3>
                                <div style={{ display: 'flex', gap: 16 }}>
                                    {[1, 2, 3].map(fase => {
                                        const f = fasesPorBanco[fase];
                                        const faseNombres = {1: 'F1: Solicitud', 2: 'F2: Respuesta', 3: 'F3: Embargo'};
                                        return (
                                            <div key={fase} style={{
                                                flex: 1,
                                                padding: 10,
                                                borderRadius: 'var(--radius-sm)',
                                                background: f ? '#F8FAF8' : '#F5F5F5',
                                                border: '1px solid var(--border-color)',
                                                textAlign: 'center',
                                                fontSize: '0.82rem',
                                                opacity: f ? 1 : 0.5,
                                            }}>
                                                <div style={{ fontWeight: 600, marginBottom: 4 }}>{faseNombres[fase]}</div>
                                                {f ? (
                                                    <>
                                                        <div><span className={`badge badge-${f.estado === 'completado' ? 'done' : f.estado === 'sin_recorrido' ? 'empty' : f.estado === 'generado' ? 'progress' : 'open'}`}>
                                                            {f.estado}
                                                        </span></div>
                                                        {f.pay_name_odoo && (
                                                            <div style={{ fontSize: '0.72rem', marginTop: 4 }}>
                                                                <strong>{f.pay_name_odoo}</strong>
                                                                {f.pay_id_odoo && ` (#${f.pay_id_odoo})`}
                                                            </div>
                                                        )}
                                                        {f.fichero_adjunto_id_odoo && (
                                                            <div style={{ fontSize: '0.7rem', color: 'var(--color-text-muted)', marginTop: 2 }}>
                                                                Adjunto Odoo #{f.fichero_adjunto_id_odoo}
                                                            </div>
                                                        )}
                                                        <div style={{ fontSize: '0.72rem', color: 'var(--color-text-muted)', marginTop: 2, display: 'flex', alignItems: 'center', gap: 4, justifyContent: 'center' }}>
                                                            {f.fichero_nombre || `${f.n_total_lineas || 0} líneas`}
                                                            {f.fichero_nombre && (
                                                                <button className="btn btn-xs btn-secondary"
                                                                    onClick={async (e) => {
                                                                        e.stopPropagation();
                                                                        try {
                                                                            const token = localStorage.getItem('token');
                                                                            const headers = token ? { 'Authorization': 'Bearer ' + token } : {};
                                                                            const resp = await fetch(endpoints.expedientes.downloadUrl(f.id), {
                                                                                credentials: 'include',
                                                                                headers
                                                                            });
                                                                            if (!resp.ok) throw new Error('Error');
                                                                            const blob = await resp.blob();
                                                                            const url = window.URL.createObjectURL(blob);
                                                                            const a = document.createElement('a'); a.href = url; a.download = f.fichero_nombre || 'fichero.txt'; a.click();
                                                                            window.URL.revokeObjectURL(url);
                                                                        } catch (err) { alert('Error al descargar'); }
                                                                    }}
                                                                    title="Descargar fichero">⬇</button>
                                                            )}
                                                        </div>
                                                    </>
                                                ) : (
                                                    <div style={{ fontSize: '0.72rem', color: 'var(--color-text-muted)' }}>pendiente</div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        ));
                    })()}
                </div>
            )}

            {/* Tab: Fase 1 */}
            {tab === 'fase1' && (
                <div className="card">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <h2>Generar Fase 1</h2>
                        <button className="btn btn-sm btn-danger"
                            onClick={async () => {
                                if (!confirm('¿Borrar todos los ficheros Fase 1 y reiniciar el expediente?')) return;
                                try {
                                    await endpoints.expedientes.resetFase1(id);
                                    setFase1Result(null);
                                    setFase1Form({ pay_id: '', bancos: [], fecha_obtencion: new Date().toISOString().split('T')[0] });
                                    setPays([]);
                                    setBancos([]);
                                    load();
                                } catch (err) {
                                    alert('Error al reiniciar: ' + (err.response?.data?.error || err.message));
                                }
                            }}
                            title="Borra los ficheros Fase 1 generados y devuelve el expediente a estado abierto">
                            ↩ Reiniciar Fase 1
                        </button>
                    </div>
                    <p style={{ color: 'var(--color-text-muted)', fontSize: '0.9rem' }}>
                        Selecciona una PAY en borrador y los bancos destino para generar los ficheros.
                    </p>

                    <button className="btn btn-sm btn-secondary" onClick={cargarPays} style={{ marginBottom: 16 }}
                        title="Consulta Odoo para obtener las PAYs en borrador disponibles para esta CR">
                        🔄 Cargar PAYs disponibles
                    </button>
                    <button className="btn btn-sm btn-secondary" onClick={cargarBancos} style={{ marginBottom: 16, marginLeft: 8 }}
                        title="Carga los bancos (NRBE) configurados para esta comunidad de regantes">
                        🏦 Cargar bancos
                    </button>

                    {pays.length > 0 && (
                        <div className="form-group">
                            <label>PAY en estado generado</label>
                            <select value={fase1Form.pay_id} onChange={e => {
                                const payId = parseInt(e.target.value);
                                const selectedPay = pays.find(p => p.id === payId);
                                setFase1Form({
                                    ...fase1Form,
                                    pay_id: payId,
                                    fecha_obtencion: selectedPay?.date_of_obtaining_the_file
                                        ? selectedPay.date_of_obtaining_the_file.slice(0, 10)
                                        : fase1Form.fecha_obtencion,
                                });
                            }}>
                                <option value="">Seleccionar...</option>
                                {pays.map(p => (
                                    <option key={p.id} value={p.id}>{p.name} ({new Date(p.create_date).toLocaleDateString()})</option>
                                ))}
                            </select>
                        </div>
                    )}

                    {bancos.length > 0 && (
                        <div className="form-group">
                            <label>Bancos destino ({bancos.length} disponibles)</label>
                            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                                {bancos.map(b => (
                                    <label key={b.id}
                                        style={{
                                            display: 'flex', alignItems: 'center', gap: 8,
                                            padding: '8px 12px',
                                            border: '1px solid var(--border-color)',
                                            borderRadius: 'var(--radius-sm)',
                                            cursor: 'pointer',
                                            background: fase1Form.bancos.includes(b.code) ? 'rgba(30,76,61,0.06)' : '#fff',
                                        }}
                                        title={`${b.name} — NRBE ${b.code} — Diario: ${b.journal_name}`}>
                                        <input type="checkbox" value={b.code}
                                            checked={fase1Form.bancos.includes(b.code)}
                                            onChange={e => {
                                                const code = e.target.value;
                                                setFase1Form({
                                                    ...fase1Form,
                                                    bancos: e.target.checked
                                                        ? [...fase1Form.bancos, code]
                                                        : fase1Form.bancos.filter(c => c !== code),
                                                });
                                            }} />
                                        <div>
                                            <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>{b.name}</div>
                                            <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>
                                                NRBE {b.code} · {b.journal_name}
                                            </div>
                                        </div>
                                    </label>
                                ))}
                            </div>
                        </div>
                    )}

                    {bancos.length > 0 && (
                        <div className="form-group">
                            <label>Fecha de obtención del fichero</label>
                            <input type="date" value={fase1Form.fecha_obtencion || ''}
                                onChange={e => setFase1Form({ ...fase1Form, fecha_obtencion: e.target.value })}
                                style={{ maxWidth: 200 }}
                                title="Fecha que aparecerá en la cabecera del fichero N63 (AAAAMMDD)" />
                        </div>
                    )}

                    <button className="btn btn-primary" onClick={generarFase1} 
                        disabled={!fase1Form.pay_id || fase1Form.bancos.length === 0}
                        title="Genera los ficheros TXT de Fase 1 para los bancos seleccionados">
                        📄 Generar ficheros Fase 1
                    </button>

                    {fase1Result && (
                        <div style={{ marginTop: 16, padding: 16, background: '#E8F5E9', borderRadius: 'var(--radius-md)' }}>
                            <h3 style={{ margin: '0 0 12px' }}>✅ {fase1Result.ficheros.length} fichero(s) generado(s)</h3>
                            {fase1Result.ficheros.map(f => (
                                <div key={f.fase_id} style={{
                                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                    padding: '8px 0', borderBottom: '1px solid rgba(0,0,0,0.08)'
                                }}>
                                    <div>
                                        <div style={{ fontWeight: 500, fontSize: '0.9rem' }}>{f.filename}</div>
                                        <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>
                                            NRBE {f.nrbe} · {f.size.toLocaleString()} bytes
                                        </div>
                                    </div>
                                    <button className="btn btn-sm btn-secondary"
                                        onClick={async () => {
                                            try {
                                                const token = localStorage.getItem('token');
                                                const headers = token ? { 'Authorization': 'Bearer ' + token } : {};
                                                const resp = await fetch(endpoints.expedientes.downloadUrl(f.fase_id), {
                                                    credentials: 'include',
                                                    headers
                                                });
                                                if (!resp.ok) throw new Error('Error al descargar');
                                                const blob = await resp.blob();
                                                const url = window.URL.createObjectURL(blob);
                                                const a = document.createElement('a');
                                                a.href = url;
                                                a.download = f.filename;
                                                a.click();
                                                window.URL.revokeObjectURL(url);
                                            } catch (err) {
                                                alert('Error al descargar: ' + err.message);
                                            }
                                        }}
                                        title="Descargar fichero TXT">
                                        ⬇ Descargar
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Tab: Fase 2 */}
            {tab === 'fase2' && (
                <div className="card">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                        <h2 style={{ margin: 0 }}>Importar Fase 2</h2>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                            <select
                                value={fase2ResetNrbe}
                                onChange={e => setFase2ResetNrbe(e.target.value)}
                                style={{ minWidth: 150 }}
                                title="Selecciona el banco cuya Fase 2 quieres borrar">
                                <option value="">Banco a reiniciar...</option>
                                {fases2Disponibles.map(f => (
                                    <option key={`${f.nrbe_entity_code}-${f.id}`} value={f.nrbe_entity_code}>
                                        {f.nrbe_entity_code}
                                    </option>
                                ))}
                            </select>
                            <button className="btn btn-sm btn-danger"
                                disabled={!fase2ResetNrbe}
                                onClick={async () => {
                                    if (!fase2ResetNrbe) return;
                                    if (!confirm(`¿Reiniciar la Fase 2 del banco ${fase2ResetNrbe}? Se borrará también cualquier Fase 3 derivada y se limpiarán los IBANs importados para ese banco.`)) return;
                                    try {
                                        await endpoints.expedientes.resetFase2(id, fase2ResetNrbe);
                                        setResultadoFase2(null);
                                        if (fase3Form.nrbe === fase2ResetNrbe) {
                                            setFase3Form({ ...fase3Form, nrbe: '' });
                                            setFase3Pendientes([]);
                                        }
                                        if (fase3ResetNrbe === fase2ResetNrbe) {
                                            setFase3ResetNrbe('');
                                        }
                                        setFase2ResetNrbe('');
                                        load();
                                    } catch (err) {
                                        alert('Error al reiniciar Fase 2: ' + (err.response?.data?.error || err.message));
                                    }
                                }}
                                title="Borra la Fase 2 importada para un banco y cualquier Fase 3 derivada">
                                ↩ Reiniciar Fase 2
                            </button>
                        </div>
                    </div>
                    <p style={{ color: 'var(--color-text-muted)', fontSize: '0.9rem' }}>
                        Sube el fichero de respuesta bancaria (Fase 2) para procesar los IBANs. La app exige que cada diligencia coincida exactamente con la Fase 1 conservada.
                    </p>

                    <div className="dropzone" onDragOver={e => e.preventDefault()}
                        onDrop={e => {
                            e.preventDefault();
                            const file = e.dataTransfer.files[0];
                            if (file) importarFase2(file);
                        }}>
                        <input type="file" accept=".txt" onChange={e => {
                            if (e.target.files[0]) importarFase2(e.target.files[0]);
                        }} style={{ display: 'none' }} id="fase2-upload" />
                        <label htmlFor="fase2-upload" style={{ cursor: 'pointer' }}>
                            <div style={{ fontSize: '1.5rem', marginBottom: 8 }}>📁</div>
                            <div><strong>Haz clic</strong> o arrastra un fichero TXT</div>
                            <div style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', marginTop: 4 }}>
                                Fichero de respuesta Fase 2 (formato N63, 650 chars/registro)
                            </div>
                        </label>
                    </div>

                    {resultadoFase2 && (
                        <div style={{ marginTop: 16, padding: 16, background: 'var(--color-surface-subtle)', borderRadius: 'var(--radius-md)' }}>
                            <h3>Resultado de la importación</h3>
                            <div className="grid-4" style={{ marginTop: 8 }}>
                                <div className="stat-card" style={{ padding: 12 }}>
                                    <div className="stat-value" style={{ fontSize: '1.5rem' }}>{resultadoFase2.resumen.total_lineas}</div>
                                    <div className="stat-label">Líneas totales</div>
                                </div>
                                <div className="stat-card" style={{ padding: 12 }}>
                                    <div className="stat-value" style={{ fontSize: '1.5rem', color: '#1E7E34' }}>{resultadoFase2.resumen.con_iban}</div>
                                    <div className="stat-label">Con IBAN</div>
                                </div>
                                <div className="stat-card" style={{ padding: 12 }}>
                                    <div className="stat-value" style={{ fontSize: '1.5rem', color: '#9E9E9E' }}>{resultadoFase2.resumen.sin_iban}</div>
                                    <div className="stat-label">Sin IBAN</div>
                                </div>
                                <div className="stat-card" style={{ padding: 12 }}>
                                    <div className="stat-value" style={{ fontSize: '1.5rem', color: '#1565C0' }}>{resultadoFase2.resumen.ibans_encontrados}</div>
                                    <div className="stat-label">IBANs encontrados</div>
                                </div>
                            </div>
                            <div style={{ marginTop: 8 }}>
                                Banco: {resultadoFase2.nrbe} · Estado: <span className={`badge badge-${resultadoFase2.estado_fase === 'completado' ? 'done' : 'empty'}`}>{resultadoFase2.estado_fase}</span>
                            </div>

                            {resultadoFase2.validacion && (
                                <div style={{ marginTop: 12, padding: 12, background: resultadoFase2.validacion.ok ? '#E8F5E9' : '#FFF3E0', borderRadius: 'var(--radius-sm)', fontSize: '0.85rem' }}>
                                    <strong>
                                        {resultadoFase2.validacion.ok
                                            ? '✅ Validación OK — NIF y diligencia coinciden con Fase 1'
                                            : '⚠️ Discrepancias encontradas'}
                                    </strong>
                                    <div style={{ marginTop: 6, display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 8 }}>
                                        <div>F1: {resultadoFase2.validacion.total_fase1} líneas</div>
                                        <div>F2: {resultadoFase2.validacion.total_fase2} líneas</div>
                                        <div style={{ color: '#1E7E34' }}>Coincidencia exacta: {resultadoFase2.validacion.match_exacto_id}</div>
                                        <div style={{ color: '#E65100' }}>Discrepancias: {(resultadoFase2.validacion.faltan_en_fase2?.length || 0) + (resultadoFase2.validacion.sobran_en_fase2?.length || 0)}</div>
                                    </div>
                                    {resultadoFase2.validacion.faltan_en_fase2?.length > 0 && (
                                        <div style={{ marginTop: 4, color: '#A10301' }}>
                                            Hay diligencias de Fase 1 que no aparecen en la Fase 2.
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* Tab: Fase 3 */}
            {tab === 'fase3' && (
                <div className="card">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                        <h2 style={{ margin: 0 }}>Generar Fase 3</h2>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                            <select
                                value={fase3ResetNrbe}
                                onChange={e => setFase3ResetNrbe(e.target.value)}
                                style={{ minWidth: 150 }}
                                title="Selecciona el banco cuya Fase 3 quieres borrar">
                                <option value="">Banco a reiniciar...</option>
                                {fases3Disponibles.map(f => (
                                    <option key={`${f.nrbe_entity_code}-${f.id}`} value={f.nrbe_entity_code}>
                                        {f.nrbe_entity_code}
                                    </option>
                                ))}
                            </select>
                            <button className="btn btn-sm btn-danger"
                                disabled={!fase3ResetNrbe}
                                onClick={async () => {
                                    if (!fase3ResetNrbe) return;
                                    if (!confirm(`¿Reiniciar la Fase 3 del banco ${fase3ResetNrbe}? Se borrará el fichero Fase 3 generado, pero se conservará la Fase 2 importada.`)) return;
                                    try {
                                        await endpoints.expedientes.resetFase3(id, fase3ResetNrbe);
                                        setFase3Result(null);
                                        if (fase3Form.nrbe === fase3ResetNrbe) {
                                            cargarPendientesF3(fase3ResetNrbe);
                                        }
                                        setFase3ResetNrbe('');
                                        load();
                                    } catch (err) {
                                        alert('Error al reiniciar Fase 3: ' + (err.response?.data?.error || err.message));
                                    }
                                }}
                                title="Borra solo la Fase 3 generada para un banco y mantiene la Fase 2">
                                ↩ Reiniciar Fase 3
                            </button>
                        </div>
                    </div>
                    <p style={{ color: 'var(--color-text-muted)', fontSize: '0.9rem' }}>
                        Selecciona el banco para el que generar la orden de embargo (Fase 3).
                    </p>

                    <div className="form-group">
                        <label>Banco (NRBE)</label>
                        <select value={fase3Form.nrbe} onChange={e => {
                            setFase3Form({ ...fase3Form, nrbe: e.target.value });
                            if (e.target.value) cargarPendientesF3(e.target.value);
                        }}>
                            <option value="">Seleccionar...</option>
                            {fases.filter(f => f.fase === 2 && f.estado === 'completado').map(f => (
                                <option key={f.id} value={f.nrbe_entity_code}>
                                    {f.nrbe_entity_code}
                                </option>
                            ))}
                        </select>
                    </div>

                    {(fase3ResumenLoading || fase3Resumen) && (
                        <div style={{ marginBottom: 16 }}>
                            {fase3ResumenLoading && (
                                <div style={{ marginBottom: 12, color: 'var(--color-text-muted)', fontSize: '0.85rem' }}>
                                    Cargando resumen de PAY origen...
                                </div>
                            )}
                            {fase3Resumen && (
                                <div>
                                    <div className="grid-4" style={{ marginBottom: 12 }}>
                                        <div className="stat-card" style={{ padding: 12 }}>
                                            <div className="stat-value" style={{ fontSize: '1.5rem' }}>
                                                {fase3Resumen.pay_origen?.bank_line_count ?? '-'}
                                            </div>
                                            <div className="stat-label">Líneas PAY F1 origen</div>
                                        </div>
                                        <div className="stat-card" style={{ padding: 12 }}>
                                            <div className="stat-value" style={{ fontSize: '1.5rem' }}>
                                                {fase3Resumen.pay_origen?.transaction_line_count ?? '-'}
                                            </div>
                                            <div className="stat-label">Transacciones PAY F1 origen</div>
                                        </div>
                                        <div className="stat-card" style={{ padding: 12 }}>
                                            <div className="stat-value" style={{ fontSize: '1.5rem' }}>
                                                {fase3Resumen.seleccion_fase3?.transaction_line_count ?? '-'}
                                            </div>
                                            <div className="stat-label">Transacciones Odoo a generar</div>
                                        </div>
                                        <div className="stat-card" style={{ padding: 12 }}>
                                            <div className="stat-value" style={{ fontSize: '1.25rem' }}>
                                                {formatCurrency(fase3Resumen.seleccion_fase3?.total_importe_fichero)}
                                            </div>
                                            <div className="stat-label">Deuda total Fase 3</div>
                                        </div>
                                    </div>

                                    <div style={{
                                        padding: 12,
                                        borderRadius: 'var(--radius-sm)',
                                        background: fase3Resumen.validation?.ok ? '#E8F5E9' : '#FFF3E0',
                                        border: '1px solid rgba(0,0,0,0.08)',
                                        marginBottom: 12,
                                        fontSize: '0.85rem',
                                    }}>
                                        <div style={{ fontWeight: 600 }}>
                                            {fase3Resumen.validation?.ok
                                                ? '✅ PAY origen y selección Fase 3 cuadran'
                                                : '⚠️ Revisa la selección: hay descuadre entre el fichero y las transacciones Odoo'}
                                        </div>
                                        <div style={{ marginTop: 6, display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                                            <div>PAY origen: <strong>{fase3Resumen.fase1?.pay_name_odoo || '-'}</strong></div>
                                            <div>Diario: <strong>{fase3Resumen.pay_origen?.journal_name || '-'}</strong></div>
                                            <div>Importe PAY F1 total: <strong>{formatCurrency(fase3Resumen.pay_origen?.total_importe)}</strong></div>
                                            <div>Importe F3 en Odoo: <strong>{formatCurrency(fase3Resumen.seleccion_fase3?.total_importe_transacciones_odoo)}</strong></div>
                                        </div>
                                    </div>

                                    <div style={{ marginBottom: 12 }}>
                                        <h3 style={{ marginBottom: 8 }}>Desglose por socio</h3>
                                        <table className="data-table">
                                            <thead>
                                                <tr>
                                                    <th>NIF</th>
                                                    <th>Nombre</th>
                                                    <th>Id deuda</th>
                                                    <th>Líneas Odoo</th>
                                                    <th>Importe Fase 3</th>
                                                    <th>Importe Odoo</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {(fase3Resumen.socios || []).map((socio) => (
                                                    <tr key={`${socio.nif}-${socio.identificador_deuda}`}>
                                                        <td>{socio.nif}</td>
                                                        <td>{socio.nombre}</td>
                                                        <td style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{socio.identificador_deuda}</td>
                                                        <td>{socio.transaction_line_count}</td>
                                                        <td>{formatCurrency(socio.importe_fichero)}</td>
                                                        <td>{formatCurrency(socio.importe_transacciones_odoo)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {fase3Pendientes.length > 0 && (
                        <div style={{ marginBottom: 16 }}>
                            <h3>Líneas pendientes ({fase3Pendientes.length})</h3>
                            <table className="data-table">
                                <thead>
                                    <tr>
                                        <th>NIF</th>
                                        <th>Nombre</th>
                                        <th>Identificador deuda</th>
                                        <th>Importe</th>
                                        <th>IBANs Fase 2</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {fase3Pendientes.map(le => (
                                        <tr key={le.id}>
                                            <td>{le.nif}</td>
                                            <td>{le.nombre}</td>
                                            <td style={{ fontSize: '0.85rem' }}>{le.identificador_deuda}</td>
                                            <td>{le.importe ? `${le.importe}€` : '-'}</td>
                                            <td style={{ fontSize: '0.8rem', minWidth: 220 }}>
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                                    {(typeof le.ibans_fase2 === 'string' ? JSON.parse(le.ibans_fase2) : le.ibans_fase2 || []).map((ibanInfo, index) => (
                                                        <div key={`${le.id}-iban-${index}`} style={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                                                            {ibanInfo.iban}
                                                        </div>
                                                    ))}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    <div className="form-group">
                        <label>Fecha de generación del fichero</label>
                        <input type="date" value={fase3Form.fecha_obtencion || ''}
                            onChange={e => setFase3Form({ ...fase3Form, fecha_obtencion: e.target.value })}
                            style={{ maxWidth: 200 }}
                            title="Fecha que aparecerá en la cabecera del fichero N63 Fase 3" />
                    </div>

                    <button className="btn btn-primary" onClick={generarFase3} disabled={!fase3Form.nrbe}
                        title="Genera las órdenes de embargo (Fase 3) para el banco seleccionado">
                        📄 Generar Fase 3 ({fase3Pendientes.length} líneas)
                    </button>

                    {fase3Result && (
                        <div style={{ marginTop: 16, padding: 16, background: '#E8F5E9', borderRadius: 'var(--radius-md)' }}>
                            <h3 style={{ margin: '0 0 8px' }}>✅ Fase 3 generada</h3>
                            <div style={{ fontSize: '0.9rem' }}>
                                <div><strong>{fase3Result.filename}</strong> · {fase3Result.total_lineas} líneas · {fase3Result.size.toLocaleString()} bytes</div>
                                {fase3Result.total_importe_fichero !== undefined && (
                                    <div style={{ marginTop: 4 }}>
                                        Importe total del fichero: <strong>{formatCurrency(fase3Result.total_importe_fichero)}</strong>
                                    </div>
                                )}
                                {fase3Result.transaction_lines_odoo > 0 && (
                                    <div style={{ marginTop: 4 }}>
                                        Líneas de transacción Odoo: <strong>{fase3Result.transaction_lines_odoo}</strong>
                                        {fase3Result.total_importe_transacciones_odoo !== undefined && (
                                            <> · Importe Odoo: <strong>{formatCurrency(fase3Result.total_importe_transacciones_odoo)}</strong></>
                                        )}
                                    </div>
                                )}
                                {fase3Result.pay_creada_odoo && (
                                    <div style={{ marginTop: 4, color: '#1E7E34' }}>
                                        PAY creada en Odoo: <strong>{fase3Result.pay_name_odoo || `#${fase3Result.pay_creada_odoo}`}</strong>
                                        {fase3Result.pay_name_odoo && ` (#${fase3Result.pay_creada_odoo})`}
                                    </div>
                                )}
                                {fase3Result.journal_name_odoo && (
                                    <div style={{ marginTop: 4 }}>
                                        Diario: <strong>{fase3Result.journal_name_odoo}</strong>
                                    </div>
                                )}
                                {fase3Result.fichero_adjunto_id_odoo && (
                                    <div style={{ marginTop: 4, color: '#1E7E34' }}>
                                        Fichero adjunto en Odoo: #{fase3Result.fichero_adjunto_id_odoo}
                                    </div>
                                )}
                                {fase3Result.pay_warning && (
                                    <div style={{ marginTop: 8, padding: 10, background: '#FFF3E0', borderRadius: 'var(--radius-sm)', color: '#8A4B00' }}>
                                        <strong>⚠️ Advertencia Odoo</strong>
                                        <div style={{ marginTop: 4 }}>{fase3Result.pay_warning.message}</div>
                                        {fase3Result.pay_warning.partial_pay_id && (
                                            <div style={{ marginTop: 4 }}>
                                                PAY parcial creada en Odoo: #{fase3Result.pay_warning.partial_pay_id}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                            <button className="btn btn-sm btn-secondary" style={{ marginTop: 8 }}
                                onClick={async () => {
                                    try {
                                        const token = localStorage.getItem('token');
                                        const headers = token ? { 'Authorization': 'Bearer ' + token } : {};
                                        const resp = await fetch(endpoints.expedientes.downloadUrl(fase3Result.fase_id), {
                                            credentials: 'include',
                                            headers
                                        });
                                        if (!resp.ok) throw new Error('Error');
                                        const blob = await resp.blob();
                                        const url = window.URL.createObjectURL(blob);
                                        const a = document.createElement('a'); a.href = url; a.download = fase3Result.filename; a.click();
                                        window.URL.revokeObjectURL(url);
                                    } catch (err) { alert('Error al descargar'); }
                                }}
                                title="Descargar fichero Fase 3">
                                ⬇ Descargar
                            </button>
                        </div>
                    )}
                </div>
            )}

            {tab === 'resultados' && <ResultadosBancarios key={id} expedienteId={id} onConfirmed={load} />}

            {/* Tab: Logs */}
            {tab === 'logs' && (
                <div className="card">
                    <h2>Trazabilidad</h2>
                    <table className="data-table">
                        <thead>
                            <tr>
                                <th>Fecha</th>
                                <th>Acción</th>
                                <th>Detalle</th>
                                <th>Usuario</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(expediente.logs || []).map(log => (
                                <tr key={log.id}>
                                    <td style={{ fontSize: '0.85rem' }}>{new Date(log.created_at).toLocaleString()}</td>
                                    <td><strong>{log.accion}</strong></td>
                                    <td style={{ fontSize: '0.85rem', maxWidth: 300, overflow: 'hidden' }}>
                                        {typeof log.detalle === 'string' ? log.detalle.slice(0, 100) : JSON.stringify(log.detalle).slice(0, 100)}
                                    </td>
                                    <td style={{ fontSize: '0.85rem' }}>{log.usuario}</td>
                                </tr>
                            ))}
                            {(!expediente.logs || expediente.logs.length === 0) && (
                                <tr><td colSpan="4" style={{ textAlign: 'center', padding: 40, color: 'var(--color-text-muted)' }}>
                                    Sin actividad registrada
                                </td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
